import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useState } from 'react';
import { useOnMount } from '../../../../common/hooks/useOnMount';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { AdminContext } from '../../../../modules/admin/AdminContext';
import { AdminTabs } from '../../../../modules/admin/components/AdminTabs';
import { ResetWorkspacesModal } from '../../../../modules/admin/components/ResetWorkspacesModal';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { Loader } from '../../../components/Loader';
import { GTable } from '../../../components/table/GTable';
import { useLoader } from '../../../helpers/useLoader';
import { IUserService } from '../../user/user.service';
import { IAdminUserInfo, IUserStore } from '../../user/user.store';
import { IWorkspaceService } from '../../workspaces/workspaces.service';
import { AdminSearch } from '../components/AdminSearch';
import { useAdminUserListSchema } from '../components/useAdminUserListSchema';

export const UsersListContainer = observer((): ReactElement => {
  const settingsContext = useContext(Settings);
  const context = useContext(AdminContext);
  const userService = useInjection(IUserService.$);
  const { adminUserInfo, adminUsersPaging } = useInjection(IUserStore.$);
  const workspaceService = useInjection(IWorkspaceService.$);

  const { t } = useTranslator();
  const { isLoading, withLoaderAsync } = useLoader();

  const [chosenUser, setChosenUser] = useState<string | null>(null);
  const [showResetWorkspaceModal, setShowResetWorkspaceModal] = useState<boolean>(false);

  const fetchData = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      await userService.getUsers(context.search);
      await userService.getPlanDefaultsAsync();
    });
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await withLoaderAsync(async () => {
      await userService.userListPaginationChange(pageNumber, pageSize, context.search);
    });
  };

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    await withLoaderAsync(async () => {
      await userService.userListOrderChange(orderBy, orderType, context.search);
    });
  };

  const handleSearchChange = async (search: string): Promise<void> => {
    await withLoaderAsync(async () => {
      await userService.userListSearchChange(search);
    });
  };

  const handleModalResetWorkspace = (userId: string): void => {
    setShowResetWorkspaceModal(true);
    setChosenUser(userId);
  };

  const handleModalResetWorkspaceCanceled = (): void => {
    setShowResetWorkspaceModal(false);
    setChosenUser(null);
  };

  const handleModalResetWorkspaceConfirmed = async (): Promise<void> => {
    if (chosenUser) {
      await withLoaderAsync(async (): Promise<void> => {
        await workspaceService.resetUserWorkspacesAsync(chosenUser);
      }, 'workspace-reset');
    }
    setShowResetWorkspaceModal(false);
    setChosenUser(null);
  };

  const handleSearchChangeDebounced = debounce(handleSearchChange, 1000, { leading: false, trailing: true }) as (search: string) => Promise<void>;

  const columns = useAdminUserListSchema({ onResetClicked: handleModalResetWorkspace });

  useOnMount(async () => {
    await fetchData();
  });

  return (
    <ColumnLayout size='none'>
      <AdminTabs />
      <S_TabsCard>
        <Loader isLoading={isLoading}>
          <ColumnLayout size='sm'>
            <AdminSearch onChange={handleSearchChangeDebounced} />
            <GTable<IAdminUserInfo>
              isLoading={isLoading}
              columns={columns}
              items={adminUserInfo}
              noItemsPlaceholder={t('no_items_admin_panel')}
              paginationProps={{
                pageNumber: adminUsersPaging.pageNumber,
                pageSize: settingsContext.store.adminPanelPageSize,
                totalCount: adminUsersPaging.totalCount,
                onChange: handlePaginationChange,
              }}
              onSortChanged={handleOrderingChange}
              sortingModel={[{ orderBy: adminUsersPaging.orderBy, orderType: adminUsersPaging.orderType }]}
            />
          </ColumnLayout>
        </Loader>
      </S_TabsCard>
      <ResetWorkspacesModal
        isOpened={showResetWorkspaceModal}
        onCanceled={handleModalResetWorkspaceCanceled}
        onConfirmed={handleModalResetWorkspaceConfirmed}
      />
    </ColumnLayout>
  );
});
